import {useCallback, useState} from 'react'
import useInlineCssVar from './use-inline-css-var'
import useViewportMeasurement from './use-viewport-measurement'

/**
 * Measures an element and publishes the value as an inline CSS custom property,
 * so recipes can use a number only the DOM knows.
 *
 * Composes `use-viewport-measurement.js` (when to read) with
 * `use-inline-css-var.js` (where the value lives). The property lands on the
 * measured element, or on `target` when another element needs to read it.
 *
 * The value outlives `active`: measuring stops, but nothing removes the property
 * until the target or the name changes, or the element unmounts. A consumer is
 * often still animating out, and dropping the value mid-animation would jump
 * back to the recipe default. The next activation measures again before paint.
 *
 * @param {string} varName - The custom property to write, e.g. `'--panel-size'`.
 * @param {function(HTMLElement): (string|null|undefined)} measure - Reads the
 *        element and returns the CSS value. `null` or `undefined` writes nothing.
 * @param {Object} [options] - Optional configuration.
 * @param {boolean} [options.active=true] - Whether to measure.
 * @param {HTMLElement} [options.target] - Element to write on. Omitted, it is
 *        the measured element. Expected to stay the same for the life of the
 *        hook — see `use-inline-css-var.js`.
 * @returns {Function} Ref callback for the element to measure.
 *
 * @example
 * const measureViewportOffset = (element) => `${Math.max(0, element.getBoundingClientRect().top)}px`
 *
 * const StickyBar = ({open}) => {
 *   const setElement = useMeasuredCssVar('--bar-viewport-offset', measureViewportOffset, {
 *     active: open
 *   })
 *
 *   return <header ref={setElement} />
 * }
 */
const useMeasuredCssVar = (varName, measure, {active = true, target} = {}) => {
    // The measured element is the default target, so it is tracked here too.
    const [element, setElement] = useState(null)

    const publish = useInlineCssVar(varName, {target: target === undefined ? element : target})
    const setMeasuredElement = useViewportMeasurement(measure, publish, {active})

    return useCallback(
        (node) => {
            setElement(node)
            setMeasuredElement(node)
        },
        [setMeasuredElement]
    )
}

export default useMeasuredCssVar
